import styled from "styled-components";
import Search from "shared/components/Search";
import { useLocation, useNavigate } from "react-router-dom";
import PLUS_SVG from "assets/images/plus.svg";
import useViewStudent from "features/teacher/hooks/lesson/useViewStudent";
import { useState } from "react";
import AttendanceManage from "./AttendanceManage";
import { hw, scrollbar } from "features/homework/homeworkUi";

const statusMeta = (status?: string) => {
    switch (status) {
        case "suspended":
            return { label: "휴학중", color: "#D97706", bg: "#FFFBEB" };
        case "graduated":
            return { label: "퇴원", color: "#64748B", bg: "#F1F5F9" };
        default:
            return { label: "재학중", color: hw.success, bg: "#F0FDF4" };
    }
};

const ViewStudent = () => {
    const lesson = useLocation().state;
    const navigate = useNavigate();
    const [clickedNav, setClickedNav] = useState("학생목록");

    return (
        <Page>
            <PageHeader>
                <div>
                    <Title>학급 상세</Title>
                    <Desc>배정된 원생을 보거나 출결을 기록합니다.</Desc>
                </div>
                <Back type="button" onClick={() => navigate("/teacher/lesson/view/lesson")}>
                    학급 목록
                </Back>
            </PageHeader>
            <Tabs>
                {["학생목록", "출결관리"].map((name) => (
                    <Tab
                        key={name}
                        type="button"
                        $on={clickedNav === name}
                        onClick={() => setClickedNav(name)}
                    >
                        {name}
                    </Tab>
                ))}
            </Tabs>
            {clickedNav === "학생목록" ? <AssignedStudent lessonId={lesson} /> : null}
            {clickedNav === "출결관리" ? <AttendanceManage lessonId={lesson} /> : null}
        </Page>
    );
};

interface AssignedStudentProps {
    lessonId: string;
}

const AssignedStudent = ({ lessonId }: AssignedStudentProps) => {
    const { searchedStudents, search, onChange } = useViewStudent(lessonId);
    const navigate = useNavigate();

    return (
        <Panel>
            <Toolbar>
                <Search placeholder="학생을 입력해 주세요" onChange={onChange} value={search} />
            </Toolbar>
            <Grid>
                <AddTile type="button" onClick={() => navigate("/teacher/lesson/manage", { state: "학급배정" })}>
                    <PlusIcon src={PLUS_SVG} alt="" />
                    <AddText>학생 추가</AddText>
                </AddTile>
                {searchedStudents.map((student) => {
                    const status = statusMeta(student.status);
                    return (
                        <Tile
                            key={student.uid}
                            type="button"
                            onClick={() => navigate("/teacher/student/profile/main", { state: student.uid })}
                        >
                            <Name>{student.name}</Name>
                            <StatusBadge $color={status.color} $bg={status.bg}>{status.label}</StatusBadge>
                        </Tile>
                    );
                })}
                {searchedStudents.length === 0 && search ? (
                    <Empty>검색 결과가 없습니다.</Empty>
                ) : null}
            </Grid>
        </Panel>
    );
};

const Page = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    padding: 20px 24px;
`;

const PageHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    flex-shrink: 0;
`;

const Title = styled.h1`
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.03em;
    color: ${hw.text};
`;

const Desc = styled.p`
    margin: 4px 0 0;
    font-size: 14px;
    color: ${hw.textMuted};
`;

const Back = styled.button`
    appearance: none;
    border: 1px solid ${hw.border};
    background: ${hw.surface};
    color: ${hw.textMuted};
    font-size: 13px;
    font-weight: 600;
    border-radius: 8px;
    padding: 8px 12px;
    cursor: pointer;

    &:hover {
        background: ${hw.bg};
        color: ${hw.text};
    }
`;

const Tabs = styled.div`
    display: flex;
    gap: 4px;
    border-bottom: 1px solid ${hw.border};
    flex-shrink: 0;
`;

const Tab = styled.button<{ $on: boolean }>`
    appearance: none;
    border: none;
    background: transparent;
    padding: 10px 14px;
    font-size: 14px;
    font-weight: 700;
    color: ${props => props.$on ? hw.brand : hw.textMuted};
    border-bottom: 2px solid ${props => props.$on ? hw.brand : "transparent"};
    margin-bottom: -1px;
    cursor: pointer;

    &:hover {
        color: ${props => props.$on ? hw.brand : hw.text};
    }
`;

const Panel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
    min-height: 0;
    padding: 18px 20px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 16px;
    box-shadow: ${hw.shadow};
`;

const Toolbar = styled.div`
    max-width: 360px;
    flex-shrink: 0;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 12px;
    align-content: start;
    flex: 1;
    min-height: 0;
    overflow: auto;
    ${scrollbar}
`;

const tileBase = `
    appearance: none;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
    min-height: 112px;
    padding: 18px 16px;
    border-radius: 14px;
    cursor: pointer;
    text-align: center;
`;

const Tile = styled.button`
    ${tileBase}
    background: ${hw.surface};
    border: 1px solid ${hw.border};

    &:hover {
        border-color: ${hw.brandMuted};
        background: ${hw.brandSoft};
    }
`;

const AddTile = styled.button`
    ${tileBase}
    background: ${hw.bg};
    border: 1px dashed ${hw.border};

    &:hover {
        border-color: ${hw.brand};
        background: ${hw.brandSoft};
    }
`;

const PlusIcon = styled.img`
    width: 22px;
    height: 22px;
    opacity: 0.55;
`;

const AddText = styled.span`
    color: ${hw.textMuted};
    font-size: 14px;
    font-weight: 600;
`;

const Name = styled.span`
    color: ${hw.text};
    font-size: 15px;
    font-weight: 700;
`;

const StatusBadge = styled.span<{ $color: string; $bg: string }>`
    display: inline-flex;
    padding: 3px 8px;
    border-radius: 999px;
    background: ${props => props.$bg};
    color: ${props => props.$color};
    font-weight: 700;
    font-size: 11px;
`;

const Empty = styled.div`
    grid-column: 1 / -1;
    padding: 40px 16px;
    text-align: center;
    color: ${hw.textSubtle};
    font-size: 13px;
`;

export default ViewStudent;
